import React, { useEffect, useState } from 'react';
import { Button } from '@synergy-marine-group/synergy-ui';
import close from '../../images/icons/cross copy.svg';
import showAlert from '../../utils/alert';
import addVendorMaster from './queries/addVendorMaster';
import updateVendorMaster from './queries/updateVendorMaster';

const emptyForm = {
  vendorName: '',
  vendorCode: '',
  sapVendorCode: '',
  address: '',
  email: ''
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

const getApiErrorMessage = (error, fallback = 'Something went wrong') => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof data?.Message === 'string' && data.Message.trim()) return data.Message.trim();
  return fallback;
};

const AddEditVendorPanel = ({
  editData,
  setEditData,
  setActivePanel
}) => {
  const isEditMode = Boolean(editData?.id);
  const { mutate: addVendor, isLoading: isAdding } = addVendorMaster();
  const { mutate: updateVendor, isLoading: isUpdating } = updateVendorMaster();
  const [form, setForm] = useState(emptyForm);
  const [initialForm, setInitialForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (editData) {
      const nextForm = {
        vendorName: editData.vendorName || '',
        vendorCode: editData.vendorCode || '',
        sapVendorCode: editData.sapVendorCode || '',
        address: editData.address || '',
        email: editData.email || ''
      };
      setForm(nextForm);
      setInitialForm(nextForm);
    } else {
      setForm(emptyForm);
      setInitialForm(emptyForm);
    }
    setFormErrors({});
  }, [editData]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!isEditMode) {
      if (!form.vendorName?.trim()) errors.vendorName = 'Vendor Name is required.';
      if (!form.vendorCode?.trim()) errors.vendorCode = 'Shipnet Vendor Code is required.';
      if (!form.sapVendorCode?.trim()) errors.sapVendorCode = 'SAP Vendor Code is required.';
    }
    if (!form.address?.trim()) errors.address = 'Address is required.';
    if (!form.email?.trim()) {
      errors.email = 'Email is required.';
    } else if (!isValidEmail(form.email)) {
      errors.email = 'Enter a valid email address.';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSuccess = (message) => {
    showAlert(message, 'success');
    setActivePanel(false);
    setEditData(null);
  };

  const handleError = (error) => {
    showAlert(getApiErrorMessage(error), 'error');
  };

  const handleSubmit = () => {
    if (!validate()) return;

    if (isEditMode) {
      updateVendor(
        {
          id: editData.id,
          vendorName: editData.vendorName,
          address: form.address.trim(),
          email: form.email.trim()
        },
        {
          onSuccess: () => handleSuccess('Vendor Updated Successfully'),
          onError: handleError
        }
      );
      return;
    }

    addVendor(
      {
        vendorName: form.vendorName.trim(),
        vendorCode: form.vendorCode.trim(),
        sapVendorCode: form.sapVendorCode.trim(),
        address: form.address.trim(),
        email: form.email.trim()
      },
      {
        onSuccess: () => handleSuccess('Vendor Added Successfully'),
        onError: handleError
      }
    );
  };

  const isSubmitting = isAdding || isUpdating;
  const isUnchanged = isEditMode
    && JSON.stringify(form) === JSON.stringify(initialForm);
  const isSaveDisabled = isSubmitting
    || (isEditMode && isUnchanged)
    || (!isEditMode && (
      !form.vendorName?.trim()
      || !form.vendorCode?.trim()
      || !form.sapVendorCode?.trim()
      || !form.address?.trim()
      || !form.email?.trim()
    ));

  const inputClass = 'w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-primary-500';
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="flex flex-col h-screen max-h-screen no-blue-focus">
      <div className="overflow-y-auto flex-1 px-6 pb-28 space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="uppercase text-center pb-2 expense-item-header font-semibold">
            {isEditMode ? 'EDIT VENDOR' : 'ADD VENDOR'}
          </h2>
          <button
            type="button"
            onClick={() => {
              setActivePanel(false);
              setEditData(null);
            }}
            className="close-btn mb-2"
          >
            <img src={close} alt="Close" width={13} height={13} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <label className="block mb-1 text-sm text-gray-100-item-label">
              Vendor Name
              {!isEditMode && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              className={isEditMode ? disabledInputClass : inputClass}
              value={form.vendorName}
              disabled={isEditMode}
              onChange={(e) => handleChange('vendorName', e.target.value)}
            />
            {formErrors.vendorName && (
              <p className="text-red-500 text-xs mt-1">{formErrors.vendorName}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-sm text-gray-100-item-label">
                Shipnet Vendor Code
                {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                className={isEditMode ? disabledInputClass : inputClass}
                value={form.vendorCode}
                disabled={isEditMode}
                onChange={(e) => handleChange('vendorCode', e.target.value)}
              />
              {formErrors.vendorCode && (
                <p className="text-red-500 text-xs mt-1">{formErrors.vendorCode}</p>
              )}
            </div>

            <div>
              <label className="block mb-1 text-sm text-gray-100-item-label">
                SAP Vendor Code
                {!isEditMode && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                className={isEditMode ? disabledInputClass : inputClass}
                value={form.sapVendorCode}
                disabled={isEditMode}
                onChange={(e) => handleChange('sapVendorCode', e.target.value)}
              />
              {formErrors.sapVendorCode && (
                <p className="text-red-500 text-xs mt-1">{formErrors.sapVendorCode}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block mb-1 text-sm text-gray-100-item-label">
              Email
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
            {formErrors.email && (
              <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block mb-1 text-sm text-gray-100-item-label">
              Address
              <span className="text-red-500">*</span>
            </label>
            <textarea
              className={`${inputClass} min-h-[5rem] resize-y`}
              value={form.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
            {formErrors.address && (
              <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>
            )}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-white px-6 py-5 mt-2 flex justify-end gap-2 border-t">
        <Button
          type="button"
          size="small"
          variant="outlined"
          rounded
          className="w-19 text-xs h-7"
          onClick={() => {
            setActivePanel(false);
            setEditData(null);
          }}
        >
          close
        </Button>
        <button
          disabled={isSaveDisabled}
          onClick={handleSubmit}
          className={`uppercase tracking-wide font-montserrat font-semibold max-w-57 px-6 rounded-lg h-8 text-xs
            focus:outline-none fill-btn
            ${isSaveDisabled
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-50'
            : 'bg-accent text-gray-100-item-label-mon hover:shadow-md active:text-white-light'}
            flex justify-center items-center`}
          type="button"
        >
          <div className="flex justify-center items-center">
            {isSubmitting ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
          </div>
        </button>
      </div>
    </div>
  );
};

export default AddEditVendorPanel;
